/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, AlertTriangle, Loader2, ScanLine, Trash2, Check, RotateCcw, Zap } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { decodeToken, foodState, fmtRemaining } from "@/lib/foodLabel";

const ELEMENT_ID = "vb-fp-reader";
const COOLDOWN_MS = 2500;     // ignore the same id within this window
const UNDO_MS = 5000;

const STATE_META = {
  ok:       { color: "#2e7d32", bg: "#dcefd2", label: "GOOD", emoji: "✓" },
  expiring: { color: "#9a3412", bg: "#fed7aa", label: "EXPIRING SOON", emoji: "!" },
  expired:  { color: "#b91c1c", bg: "#fecaca", label: "DISPOSE NOW", emoji: "✕" },
  disposed: { color: "#6d28d9", bg: "#ede9fe", label: "DISPOSED", emoji: "🗑" },
  kept:     { color: "#2e7d32", bg: "#dcefd2", label: "KEPT — STILL GOOD", emoji: "✓" },
  flagged:  { color: "#b45309", bg: "#fde68a", label: "MARKED FOR DISPOSAL", emoji: "⚑" },
  unknown:  { color: "#57534e", bg: "#e7e5e4", label: "UNKNOWN LABEL", emoji: "?" },
};

/**
 * Dual-mode continuous food scanner.
 *  • Quick Check  — instant 🟢/🟡/🔴 verdict, resolved against a local cache
 *    (no per-scan network); auto-refreshes every 45 s.
 *  • Active Track — scan-to-dispose with a 5-second Undo.
 */
export default function FoodPrepScanner({ onDisposed }) {
  const { confirm } = useDialog();
  const [step, setStep] = useState("scanning"); // scanning | denied | error
  const [mode, setMode] = useState("quick");     // quick | dispose
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);     // { id, name, state, mins }
  const [undoItem, setUndoItem] = useState(null);
  const [counts, setCounts] = useState({ scanned: 0, disposed: 0 });
  const [cacheReady, setCacheReady] = useState(false);
  const [thresholdMins, setThresholdMins] = useState(0);

  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const cacheRef = useRef(new Map());     // id -> item
  const lastSeenRef = useRef({});         // id -> ts
  const modeRef = useRef(mode);
  const thresholdRef = useRef(0);
  const busyRef = useRef(false);
  const undoTimerRef = useRef(null);
  const flashRef = useRef(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { thresholdRef.current = thresholdMins; }, [thresholdMins]);

  useEffect(() => {
    api.get("/food-prep/settings")
      .then(({ data }) => setThresholdMins(data.dispose_confirm_min_shelf_life_minutes || 0))
      .catch(() => {});
  }, []);

  const loadCache = useCallback(async () => {
    try {
      const { data } = await api.get("/food-prep/scan-cache");
      const m = new Map();
      (data.items || []).forEach((it) => m.set(it.id, it));
      cacheRef.current = m;
      setCacheReady(true);
    } catch (e) {
      console.debug("scan-cache load failed:", e?.response?.status);
    }
  }, []);

  const stop = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { console.debug(e); }
      try { await scannerRef.current.clear(); } catch (e) { console.debug(e); }
      scannerRef.current = null;
    }
  };

  const start = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStep("scanning"); setErr("");
    try {
      const config = { fps: 10, qrbox: { width: 240, height: 240 }, rememberLastUsedCamera: true };
      const qr = new Html5Qrcode(ELEMENT_ID, false);
      scannerRef.current = qr;
      await qr.start({ facingMode: "environment" }, config, onDecode, () => {});
    } catch (e) {
      const name = e?.name || "";
      const msg = (e?.message || "").toLowerCase();
      if (name === "NotAllowedError" || msg.includes("permission")) setStep("denied");
      else { setErr(e?.message || "Could not start camera."); setStep("error"); }
      startedRef.current = false;
    }
  };

  useEffect(() => {
    loadCache();
    const refresh = setInterval(loadCache, 45000);
    const t = setTimeout(start, 60);
    return () => {
      clearInterval(refresh); clearTimeout(t);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      if (flashRef.current) clearTimeout(flashRef.current);
      stop();
    };
  }, []);

  const doFlash = (state) => {
    setFlash(state);
    if (flashRef.current) clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setFlash(null), 650);
  };

  const resolveItem = async (id) => {
    const cached = cacheRef.current.get(id);
    if (cached) return cached;
    try {
      const { data } = await api.post("/scan/resolve", { token: `VBE1:${btoa(JSON.stringify({ t: "fp", id })).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}` });
      if (data?.kind === "food_prep" && data.item) {
        if (data.item.status === "active") cacheRef.current.set(id, data.item);
        return data.item;
      }
    } catch (e) {
      console.debug("resolve fallback failed:", e?.response?.status);
    }
    return null;
  };

  const onDecode = async (text) => {
    const decoded = decodeToken(text);
    if (!decoded || decoded.t !== "fp") {
      setResult({ state: "unknown", name: "Not a food label" });
      doFlash("unknown");
      return;
    }
    const id = decoded.id;
    const now = Date.now();
    if (lastSeenRef.current[id] && now - lastSeenRef.current[id] < COOLDOWN_MS) return;
    lastSeenRef.current[id] = now;

    const item = await resolveItem(id);
    if (!item) {
      setResult({ state: "unknown", name: "Unknown / not in this site" });
      doFlash("unknown");
      return;
    }
    const { state, minutes_remaining } = foodState(item);
    const flagged = !!item.flagged_for_disposal;
    setCounts((c) => ({ ...c, scanned: c.scanned + 1 }));

    if (modeRef.current === "dispose") {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        // Guard: if the item still has more shelf life than the configured
        // minimum, ask the user to confirm before disposing — UNLESS it was
        // deliberately marked for disposal.
        const thr = thresholdRef.current;
        if (!flagged && thr > 0 && minutes_remaining != null && minutes_remaining > thr) {
          doFlash("expiring");
          const ok = await confirm({
            title: "Dispose this item?",
            message: `“${item.name}” still has ${fmtRemaining(minutes_remaining)} of shelf life left — above your ${fmtRemaining(thr)} minimum. Dispose it anyway?`,
            danger: true, confirmLabel: "Dispose", cancelLabel: "Keep",
          });
          if (!ok) {
            setResult({ id, name: item.name, state: "kept", mins: minutes_remaining });
            doFlash("ok");
            busyRef.current = false;
            return;
          }
        }
        // Deduct one unit from the batch (shared QR).
        const { data } = await api.post(`/food-prep/${id}/dispose`, { reason: flagged ? "marked_disposal" : "scanned_disposal", qty: 1 });
        const remaining = data?.remaining ?? 0;
        if (remaining > 0) cacheRef.current.set(id, { ...item, quantity_remaining: remaining });
        else cacheRef.current.delete(id);
        setResult({ id, name: item.name, state: "disposed", mins: minutes_remaining, prev: state, remaining });
        doFlash("disposed");
        setCounts((c) => ({ ...c, disposed: c.disposed + 1 }));
        showUndo({ id, name: item.name });
        onDisposed?.();
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || "Dispose failed");
      } finally {
        busyRef.current = false;
      }
    } else if (flagged) {
      setResult({ id, name: item.name, state: "flagged", mins: minutes_remaining });
      doFlash("flagged");
    } else {
      setResult({ id, name: item.name, state, mins: minutes_remaining });
      doFlash(state);
    }
  };

  const showUndo = (it) => {
    setUndoItem(it);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setUndoItem(null), UNDO_MS);
  };

  const undo = async () => {
    if (!undoItem) return;
    const it = undoItem;
    setUndoItem(null);
    try {
      const { data } = await api.post(`/food-prep/${it.id}/undo-dispose`);
      if (data?.item) cacheRef.current.set(it.id, data.item);
      setCounts((c) => ({ ...c, disposed: Math.max(0, c.disposed - 1) }));
      setResult(null);
      onDisposed?.();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Undo failed");
    }
  };

  const retry = async () => { await stop(); startedRef.current = false; start(); };

  const meta = result ? (STATE_META[result.state] || STATE_META.unknown) : null;
  const frameColor = flash ? (STATE_META[flash]?.color || "#57534e") : "transparent";

  return (
    <div data-testid="food-prep-scanner" className="fp-touch">
      {/* Mode toggle */}
      <div className="flex gap-2 mb-3 items-center fp-scan-toggle" role="tablist" data-testid="scanner-mode-toggle">
        <button
          className={`vb-btn sm ${mode === "quick" ? "" : "ghost"}`}
          onClick={() => setMode("quick")}
          data-testid="scanner-mode-quick"
        >
          <ScanLine size={15} /> Quick Check
        </button>
        <button
          className={`vb-btn sm ${mode === "dispose" ? "danger" : "ghost"}`}
          onClick={() => setMode("dispose")}
          data-testid="scanner-mode-dispose"
        >
          <Trash2 size={15} /> Active Tracking (Dispose)
        </button>
      </div>

      {mode === "dispose" && (
        <div className="vb-pill err mb-3" style={{ width: "100%", justifyContent: "center", padding: "8px" }} data-testid="scanner-dispose-banner">
          <AlertTriangle size={14} />
          {thresholdMins > 0
            ? <span>ACTIVE TRACKING — items with more than {fmtRemaining(thresholdMins)} left ask for confirmation</span>
            : <span>ACTIVE TRACKING — every scanned item will be disposed</span>}
        </div>
      )}

      {step === "scanning" && (
        <div data-testid="scanner-active">
          <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", background: "#000" }}>
            <div id={ELEMENT_ID} className="fp-cam" style={{ minHeight: 300 }} />
            <div
              style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                border: `10px solid ${frameColor}`, borderRadius: 14,
                transition: "border-color 120ms ease", boxSizing: "border-box",
              }}
            />
          </div>

          {/* Verdict banner */}
          {result && (
            <div
              className="mt-3 rounded-xl p-4 flex items-center justify-between"
              style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}33` }}
              data-testid="scanner-verdict"
            >
              <div>
                <div className="text-xs font-semibold opacity-80">{meta.label}</div>
                <div className="text-lg font-bold leading-tight" data-testid="scanner-verdict-name">{result.name}</div>
                {result.mins != null && result.state !== "disposed" && (
                  <div className="text-sm">{fmtRemaining(result.mins)}</div>
                )}
                {result.state === "disposed" && (
                  <div className="text-sm">{result.remaining > 0 ? `Disposed 1 · ${result.remaining} left in batch` : "Disposed — none left in batch"}</div>
                )}
              </div>
              <div style={{ fontSize: 40, lineHeight: 1 }}>{meta.emoji}</div>
            </div>
          )}

          {undoItem && (
            <div className="mt-2 flex items-center justify-between rounded-lg px-3 py-2"
                 style={{ background: "#1c1917", color: "#fafaf9" }} data-testid="scanner-undo">
              <span className="text-sm">Disposed “{undoItem.name}”.</span>
              <button className="vb-btn xs warn fp-undo-btn" onClick={undo} data-testid="scanner-undo-btn">
                <RotateCcw size={13} /> Undo
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mt-3 text-xs opacity-70">
            <span className="flex items-center gap-1">
              {cacheReady ? <Zap size={12} /> : <Loader2 size={12} className="animate-spin" />}
              {cacheRef.current.size} active in this site
            </span>
            <span data-testid="scanner-counts">Scanned {counts.scanned} · Disposed {counts.disposed}</span>
          </div>
          <p className="text-xs opacity-60 mt-2 text-center">Hold each label steady inside the frame.</p>
          {err && <div className="vb-pill err mt-2"><AlertTriangle size={13} /> {err}</div>}
        </div>
      )}

      {step === "denied" && (
        <div data-testid="scanner-denied" className="vb-card">
          <div className="vb-pill err mb-3"><AlertTriangle size={14} /> Camera access was blocked</div>
          <p className="text-sm opacity-80 mb-3">Enable camera access for this site in your browser settings, then tap Try again.</p>
          <button className="vb-btn" onClick={retry}><Camera size={15} /> Try again</button>
        </div>
      )}

      {step === "error" && (
        <div data-testid="scanner-error" className="vb-card">
          <div className="vb-pill err mb-3"><AlertTriangle size={14} /> {err || "Something went wrong"}</div>
          <button className="vb-btn" onClick={retry}><Camera size={15} /> Try again</button>
        </div>
      )}
    </div>
  );
}
